import { useEffect, useState } from 'react';
import { Pin, Building2, Trash2 } from 'lucide-react';
import Modal from '../common/Modal';
import useAsync from '../../hooks/useAsync';
import pinService from '../../services/pinService';
import adminService from '../../services/adminService';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/format';
import cn from '../../utils/cn';

/**
 * Put a document on other people's screens.
 *
 * The note is asked for first and given the most room, because it is the difference between
 * a pin and a bookmark somebody else chose for you. "Renewed — use this one, not the 2025
 * copy" answers the question a reader will otherwise ask a colleague.
 *
 * Only the scopes this person may pin into are offered. Showing an organisation option to
 * somebody who will be refused it is a way of teaching people that the interface lies.
 */
export default function PinDialog({ open, document: doc, onClose, onChanged }) {
  const toast = useToast();
  const { data: scopes } = useAsync(() => (open ? pinService.scopes() : Promise.resolve([])), [open]);
  const { data: departments } = useAsync(() => (open ? adminService.listDepartments() : Promise.resolve([])), [open]);
  const { data: existing, reload } = useAsync(
    () => (open && doc ? pinService.forDocument(doc.id) : Promise.resolve([])),
    [open, doc?.id],
  );

  const [target, setTarget] = useState(null);
  const [note, setNote] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);

  const allowed = scopes || [];
  const deptName = (id) => (departments || []).find((d) => d.id === id)?.name || 'Department';

  // Default to the narrowest scope offered. Pinning company-wide by accident is the mistake
  // worth making hard; it lands on every screen in the building.
  useEffect(() => {
    if (!open || !allowed.length || target) return;
    const narrowest = allowed.find((s) => s.scope === 'department') || allowed[0];
    setTarget(narrowest);
  }, [open, allowed, target]);

  useEffect(() => {
    if (!open) {
      setTarget(null);
      setNote('');
      setExpiresAt('');
    }
  }, [open]);

  const submit = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await pinService.pin(doc.id, {
        scope: target.scope,
        departmentId: target.departmentId,
        note: note.trim(),
        // The input gives a date; the API wants an instant. End of that day, so "expires on
        // the 30th" means the 30th is still included.
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
      });
      toast.success(target.scope === 'organization' ? 'Pinned for everyone' : `Pinned for ${deptName(target.departmentId)}`);
      setNote('');
      setExpiresAt('');
      reload();
      onChanged?.();
    } catch (err) {
      toast.error(err.message || 'That could not be pinned.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (pin) => {
    try {
      await pinService.unpin(pin.id);
      toast.success('Unpinned');
      reload();
      onChanged?.();
    } catch (err) {
      toast.error(err.message || 'That could not be unpinned.');
    }
  };

  if (!doc) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={<Pin size={18} />}
      title={`Pin “${doc.name}”`}
      subtitle="Puts it at the top of the dashboard for the people you choose."
      width={520}
      footer={
        <>
          <button type="button" className="btn btn-g" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn btn-p" onClick={submit} disabled={busy || !target}>
            {busy ? 'Pinning…' : 'Pin it'}
          </button>
        </>
      }
    >
      {allowed.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Pinning is for department managers and administrators. Ask one of them to pin this if
          it should be on everyone’s dashboard.
        </p>
      ) : (
        <div className="stack" style={{ gap: 14 }}>
          <div>
            <label className="lbl">Who sees it</label>
            <div className="stack" style={{ gap: 6 }}>
              {allowed.map((s) => {
                const key = `${s.scope}:${s.departmentId || ''}`;
                const active = target && `${target.scope}:${target.departmentId || ''}` === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={cn('side-list__item', active && 'is-active')}
                    style={{ textAlign: 'left', width: '100%', cursor: 'pointer' }}
                    onClick={() => setTarget(s)}
                  >
                    <span className="row" style={{ gap: 8 }}>
                      {s.scope === 'organization' && <Building2 size={14} />}
                      <span style={{ fontSize: 13.5, fontWeight: active ? 600 : 400 }}>
                        {s.scope === 'organization' ? 'Everyone in the organisation' : deptName(s.departmentId)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="lbl" htmlFor="pin-note">Why it matters</label>
            <input
              id="pin-note"
              className="in"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Renewed — use this one, not the 2025 copy"
              maxLength={280}
            />
            <div className="muted" style={{ fontSize: 11.5, marginTop: 5 }}>
              Shown under the name. Worth writing — without it people see a document on their
              dashboard and no reason for it being there.
            </div>
          </div>

          <div>
            <label className="lbl" htmlFor="pin-expiry">Unpin on (optional)</label>
            <input
              id="pin-expiry"
              className="in"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)}
            />
            <div className="muted" style={{ fontSize: 11.5, marginTop: 5 }}>
              For anything with an end date — a holiday notice, a deadline. Leave empty to keep
              it until somebody removes it.
            </div>
          </div>

          {(existing || []).length > 0 && (
            <div>
              <label className="lbl">Already pinned</label>
              <div className="panel" style={{ overflow: 'hidden' }}>
                {existing.map((p) => (
                  <div key={p.id} className="side-list__item">
                    <span className="stack" style={{ gap: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13 }}>
                        {p.scope === 'organization' ? 'Everyone' : p.department?.name}
                      </span>
                      <span className="muted" style={{ fontSize: 11.5 }}>
                        {p.note || `by ${p.pinnedBy || 'someone'}`}
                        {p.expiresAt ? ` · unpins ${formatDate(p.expiresAt)}` : ''}
                      </span>
                    </span>
                    {p.canRemove && (
                      <button type="button" className="btn--icon" style={{ width: 26, height: 26 }} aria-label="Unpin" onClick={() => remove(p)}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
